import {
    Plan,
    PlanTrain,
    PlanInference,
    PlanAutoLabel,
    PlanVideoInference,
    PlanDeploy
} from "../types";

/*
 Almost all of these limits are only applicable to non-UBP, non-credit system plans.

 What these limits mean in non credit system workspaces:
 - Train refers to model training credits which reloaded or rolled over.
 - Inferences were the number of Serverless V1 inferences a user could run (V1 was only execution count, V2 is execution time based and therefore not supported on non-credit system workspaces)
 
 Only tfjs, Oak, TensorRT, and CoreML are relevant on non-credit system workspaces.
 */

// Training
export const PLAN_TRAIN: Record<Plan, Partial<PlanTrain>> = {
    [Plan.Public]: { quota: 3, used: 0, pt: false, modelLicense: 0 },
    [Plan.UbpBasic]: {
        quota: 5,
        used: 0,
        reload: 0,
        accurate: true,
        rollover: false,
        pt: true,
        modelLicense: 0
    },
    [Plan.UbpCore]: {
        quota: 5,
        used: 0,
        reload: 0,
        accurate: true,
        rollover: false,
        pt: true,
        modelLicense: 0
    },
    [Plan.UbpGrowth]: {
        quota: 10,
        used: 0,
        reload: 5,
        accurate: true,
        rollover: false,
        pt: true,
        modelLicense: 10
    },
    [Plan.UbpPremiumTrial]: {
        quota: 10,
        used: 0,
        reload: 5,
        accurate: true,
        rollover: false,
        pt: false,
        modelLicense: 0
    },
    [Plan.EnterpriseGrowth]: { quota: "Custom", used: 0, pt: true, modelLicense: "Custom" } as any,
    [Plan.Enterprise]: { quota: "Custom", used: 0, pt: true, modelLicense: "Custom" },
    [Plan.LegacySandbox]: {
        quota: 3,
        used: 0,
        reload: 0,
        rollover: false,
        pt: false,
        modelLicense: 0
    },
    [Plan.LegacyStarter]: {
        quota: 10,
        used: 0,
        reload: 5,
        accurate: true,
        rollover: false,
        pt: false,
        modelLicense: 0
    },
    [Plan.Research]: { quota: 3, used: 0, pt: true, modelLicense: 0 },
    [Plan.UninitalizedWorkspace]: { quota: 0, used: 0, pt: false, modelLicense: 0 }
} as any;

// Inference (legacy non-UBP)
export const DEFAULT_INFERENCE: PlanInference = {
    soft: false,
    quota: 1000,
    used: 0,
    billing_period: "monthly",
    last_billed_on: null,
    replenish_after_billing: true
};
export const PLAN_INFERENCE: Record<Plan, Partial<PlanInference>> = {
    [Plan.Public]: { quota: 10000 },
    [Plan.UbpBasic]: { quota: 25000 },
    [Plan.UbpCore]: { quota: 25000 },
    [Plan.UbpGrowth]: { quota: 25000 },
    [Plan.UbpPremiumTrial]: { quota: 25000 },
    [Plan.EnterpriseGrowth]: { quota: "Custom" } as any,
    [Plan.Enterprise]: { quota: "Custom" },
    [Plan.LegacySandbox]: {},
    [Plan.LegacyStarter]: { quota: 25000 },
    [Plan.Research]: { quota: 25000 },
    [Plan.UninitalizedWorkspace]: { quota: 1000 }
} as any;

// Auto Label (legacy non-UBP)
export const DEFAULT_AUTO_LABEL: PlanAutoLabel = {
    monthly_quota: 1000,
    monthly_used: 0,
    purchased_credits: 0,
    purchased_used: 0
};
export const PLAN_AUTO_LABEL: Record<Plan, Partial<PlanAutoLabel>> = {
    [Plan.Public]: {},
    [Plan.UbpBasic]: {},
    [Plan.UbpCore]: {},
    [Plan.UbpGrowth]: { monthly_quota: 10000 },
    [Plan.UbpPremiumTrial]: { monthly_quota: 10000 },
    [Plan.EnterpriseGrowth]: { monthly_quota: "Custom" } as any,
    [Plan.Enterprise]: { monthly_quota: "Custom" },
    [Plan.LegacySandbox]: {},
    [Plan.LegacyStarter]: { monthly_quota: 10000 },
    [Plan.Research]: {},
    [Plan.UninitalizedWorkspace]: {}
} as any;

// Video Inference (legacy non-UBP)
export const DEFAULT_VIDEO_INFERENCE: PlanVideoInference = { quota: 5, used: 0 };
export const PLAN_VIDEO_INFERENCE: Record<Plan, Partial<PlanVideoInference>> = {
    [Plan.UbpGrowth]: { quota: 25, used: 0 },
    [Plan.UbpPremiumTrial]: { quota: 25, used: 0 },
    [Plan.EnterpriseGrowth]: { quota: "Custom", used: 0 } as any,
    [Plan.Enterprise]: { quota: "Custom", used: 0 } as any,
    [Plan.LegacyStarter]: { quota: 25, used: 0 }
} as any;

// TFJS
export const DEFAULT_TFJS: PlanDeploy = { limit: 1000, mau: 0 };
export const PLAN_TFJS: Record<Plan, Partial<PlanDeploy>> = {
    [Plan.EnterpriseGrowth]: { limit: "Custom", mau: 0 } as any,
    [Plan.Enterprise]: { limit: "Custom", mau: 0 } as any
} as any;

// Oak
export const DEFAULT_OAK: PlanDeploy = { limit: 1, mau: 0 };
export const PLAN_OAK: Record<Plan, Partial<PlanDeploy>> = {
    [Plan.Public]: { limit: 5, mau: 0 },
    [Plan.Research]: { limit: 10, mau: 0 },
    [Plan.EnterpriseGrowth]: { limit: "Custom", mau: 0 } as any,
    [Plan.Enterprise]: { limit: "Custom", mau: 0 } as any
} as any;

// TensorRT
export const DEFAULT_TENSORRT: PlanDeploy = { limit: 5, mau: 0 };
export const PLAN_TENSORRT: Record<Plan, Partial<PlanDeploy>> = {
    [Plan.EnterpriseGrowth]: { limit: "Custom", mau: 0 } as any,
    [Plan.Enterprise]: { limit: "Custom", mau: 0 } as any
} as any;

// CoreML
export const DEFAULT_COREML: PlanDeploy = { limit: 1, mau: 0 };
export const PLAN_COREML: Record<Plan, Partial<PlanDeploy>> = {
    [Plan.EnterpriseGrowth]: { limit: "Custom", mau: 0 } as any,
    [Plan.Enterprise]: { limit: "Custom", mau: 0 } as any
} as any;

export const TRAIN_CONFIG = PLAN_TRAIN;

export const INFERENCE_CONFIG: Record<Plan, PlanInference> = Object.keys(PLAN_INFERENCE).reduce(
    (acc, plan) => {
        acc[plan] = { ...DEFAULT_INFERENCE, ...PLAN_INFERENCE[plan] };
        return acc;
    },
    {} as Record<Plan, PlanInference>
);

export const AUTO_LABEL_CONFIG: Record<Plan, PlanAutoLabel> = Object.keys(PLAN_AUTO_LABEL).reduce(
    (acc, plan) => {
        acc[plan] = { ...DEFAULT_AUTO_LABEL, ...PLAN_AUTO_LABEL[plan] };
        return acc;
    },
    {} as Record<Plan, PlanAutoLabel>
);

export const VIDEO_INFERENCE_CONFIG: Record<Plan, PlanVideoInference> = Object.values(Plan).reduce(
    (acc, plan) => {
        if (plan !== undefined) {
            acc[plan] = { ...DEFAULT_VIDEO_INFERENCE, ...(PLAN_VIDEO_INFERENCE[plan] || {}) };
        }
        return acc;
    },
    {} as Record<Plan, PlanVideoInference>
);

export const TFJS_CONFIG: Record<Plan, PlanDeploy> = Object.values(Plan).reduce(
    (acc, plan) => {
        if (plan !== undefined) {
            acc[plan] = { ...DEFAULT_TFJS, ...(PLAN_TFJS[plan] || {}) };
        }
        return acc;
    },
    {} as Record<Plan, PlanDeploy>
);

export const OAK_CONFIG: Record<Plan, PlanDeploy> = Object.values(Plan).reduce(
    (acc, plan) => {
        if (plan !== undefined) {
            acc[plan] = { ...DEFAULT_OAK, ...(PLAN_OAK[plan] || {}) };
        }
        return acc;
    },
    {} as Record<Plan, PlanDeploy>
);

export const TENSORRT_CONFIG: Record<Plan, PlanDeploy> = Object.values(Plan).reduce(
    (acc, plan) => {
        if (plan !== undefined) {
            acc[plan] = { ...DEFAULT_TENSORRT, ...(PLAN_TENSORRT[plan] || {}) };
        }
        return acc;
    },
    {} as Record<Plan, PlanDeploy>
);

export const COREML_CONFIG: Record<Plan, PlanDeploy> = Object.values(Plan).reduce(
    (acc, plan) => {
        if (plan !== undefined) {
            acc[plan] = { ...DEFAULT_COREML, ...(PLAN_COREML[plan] || {}) };
        }
        return acc;
    },
    {} as Record<Plan, PlanDeploy>
);
